import { useCallback, useEffect, useRef } from "react";

/** Stagger between cards that scroll into view together — matches the audit timeline's cadence. */
export const SCROLL_REVEAL_STAGGER_MS = 55;

/**
 * Reveals `[data-reveal]` children as they scroll into view, the same fade-and-rise the user/studio
 * history timelines use, but driven by the viewport instead of mount so long lists animate as you
 * scroll. Each child starts `data-revealed="false"`; when it enters the viewport it flips to
 * "true", and a batch that arrives together is staggered via `transition-delay`. The CSS owns the
 * actual transition (see the `[data-reveal]` rules in the section styles).
 *
 * Pass a `dependency` that changes when the list changes (e.g. its length) so new rows get observed.
 * Falls back to revealing everything immediately where IntersectionObserver is unavailable.
 */
export const useScrollReveal = <T extends HTMLElement>(dependency: unknown) => {
  const containerRef = useRef<T | null>(null);

  const reveal = useCallback((element: HTMLElement, indexInBatch: number) => {
    element.style.transitionDelay = `${indexInBatch * SCROLL_REVEAL_STAGGER_MS}ms`;
    element.setAttribute("data-revealed", "true");
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const children = Array.from(
      container.querySelectorAll<HTMLElement>("[data-reveal]"),
    ).filter((child) => child.getAttribute("data-revealed") !== "true");

    if (children.length === 0) {
      return;
    }

    // Without IntersectionObserver (old browsers, some test envs) show everything right away
    // rather than leaving the list invisible.
    if (typeof IntersectionObserver === "undefined") {
      children.forEach((child) => child.setAttribute("data-revealed", "true"));
      return;
    }

    children.forEach((child) => child.setAttribute("data-revealed", "false"));

    const observer = new IntersectionObserver((entries) => {
      const arriving = entries.filter((entry) => entry.isIntersecting);

      arriving.forEach((entry, index) => {
        reveal(entry.target as HTMLElement, index);
        observer.unobserve(entry.target);
      });
    });

    children.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [dependency, reveal]);

  return containerRef;
};
